import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiUser, FiFileText, FiUserCheck } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';

const ExamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchExam();
  }, [id]);

  const fetchExam = async () => {
    try {
      const response = await api.get(`/exams/${id}`);
      setExam(response.data.data);
    } catch (error) {
      toast.error('Không thể tải thông tin ca chụp');
      navigate('/exams');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/exams/${id}/sync`);
      toast.success('Đồng bộ ca chụp thành công');
      fetchExam();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể đồng bộ ca chụp');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
        <p className="text-slate-500 text-sm">Đang tải thông tin ca chụp...</p>
      </div>
    );
  }

  if (!exam) {
    return <div className="text-center p-6 text-red-600 text-sm">Không tìm thấy ca chụp</div>;
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Đã lên lịch', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', class: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Hoàn thành', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Đã hủy', class: 'bg-red-100 text-red-800' },
      synced: { label: 'Đã đồng bộ', class: 'bg-indigo-100 text-indigo-800' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-blue-100 text-blue-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 animate-fade-in">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <button
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white text-slate-700 rounded border border-slate-200 text-xs font-medium hover:bg-slate-50 transition-colors"
            onClick={() => navigate('/exams')}
          >
            <FiArrowLeft className="text-base" />
            Quay lại
          </button>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Chi tiết ca chụp</div>
            <div className="text-lg font-semibold text-slate-900 flex flex-wrap items-center gap-2">
              <span>{exam.patient_name || exam.patient_id || '-'}</span>
              {exam.accession_number && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
                  Accession: {exam.accession_number}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span>Mã BN: <span className="font-medium text-slate-700">{exam.patient_id || '-'}</span></span>
              {exam.external_id && (
                <span className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-slate-300" /> HIS: <span className="font-medium text-slate-700">{exam.external_id}</span>
                </span>
              )}
              {exam.modality && (
                <span className="flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-slate-300" /> Modality: <span className="font-medium text-slate-700">{exam.modality}</span>
                </span>
              )}
            </div>
          </div>
          {exam.status !== 'synced' && (
            <button
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded text-xs font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Đang đồng bộ...
                </>
              ) : (
                <>
                  <FiUpload className="text-base" />
                  Đồng bộ lên Cloud
                </>
              )}
            </button>
          )}
        </div>

        {/* Main cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-200">
            <FiUser className="text-primary text-lg" />
            <h2 className="text-sm font-semibold text-slate-900 m-0">Thông tin bệnh nhân</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Tên</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_name || '-'}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Mã bệnh nhân</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_id || '-'}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Ngày sinh</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_birth_date || '-'}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Giới tính</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_sex || '-'}</span>
            </div>
            {exam.patient_age && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Tuổi</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_age}</span>
              </div>
            )}
            {exam.patient_weight && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Cân nặng</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_weight} kg</span>
              </div>
            )}
            {exam.patient_address && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Địa chỉ</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_address}</span>
              </div>
            )}
            {exam.patient_phone && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Số điện thoại</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.patient_phone}</span>
              </div>
            )}
            {exam.cccd && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">CCCD</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.cccd}</span>
              </div>
            )}
          </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
            <FiFileText className="text-primary text-lg" />
            <h2 className="text-sm font-semibold text-slate-900 m-0">Thông tin ca chụp</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Study Instance UID</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4 break-words">{exam.study_instance_uid || '-'}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Accession Number</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.accession_number || '-'}</span>
            </div>
            {exam.external_id && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Mã chỉ định HIS</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.external_id}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-slate-600">Loại máy</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                {exam.modality || '-'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-slate-600">Trạng thái</span>
              {getStatusBadge(exam.status)}
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Thời gian lên lịch</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{formatDateTime(exam.scheduled_time)}</span>
            </div>
            {exam.study_date && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Ngày chụp</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">
                  {exam.study_date} {exam.study_time ? exam.study_time.substring(0, 5) : ''}
                </span>
              </div>
            )}
            {exam.completed_at && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Hoàn thành</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">
                  {formatDateTime(exam.completed_at)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-start">
              <span className="text-xs font-medium text-slate-600">Cơ sở</span>
              <span className="text-sm text-slate-900 text-right flex-1 ml-4">{exam.site_name || '-'}</span>
            </div>
            {exam.station_name && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Máy XQ</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">
                  {exam.station_name}
                  {exam.station_ae_title && (
                    <code className="text-xs bg-slate-100 text-slate-700 rounded px-1 py-0.5 ml-1">{exam.station_ae_title}</code>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-200">
          <FiFileText className="text-primary text-lg" />
          <h2 className="text-sm font-semibold text-slate-900 m-0">Thông tin chỉ định</h2>
        </div>
        <div className="space-y-3">
          {exam.description && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Mô tả</span>
              <span className="text-sm text-slate-900">{exam.description}</span>
            </div>
          )}
          {exam.reason_for_study && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Lý do chỉ định</span>
              <span className="text-sm text-slate-900">{exam.reason_for_study}</span>
            </div>
          )}
          {exam.clinical_info && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Thông tin lâm sàng</span>
              <span className="text-sm text-slate-900 whitespace-pre-wrap">{exam.clinical_info}</span>
            </div>
          )}
          {exam.procedure_code && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Mã thủ thuật</span>
              <span className="text-sm text-slate-900 bg-slate-100 px-2 py-1 rounded">{exam.procedure_code}</span>
            </div>
          )}
          {exam.procedure_description && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Mô tả thủ thuật</span>
              <span className="text-sm text-slate-900">{exam.procedure_description}</span>
            </div>
          )}
          {exam.service_id && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Mã dịch vụ</span>
              <span className="text-sm text-slate-900">{exam.service_id}</span>
            </div>
          )}
          {exam.service_name && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Tên dịch vụ</span>
              <span className="text-sm text-slate-900">{exam.service_name}</span>
            </div>
          )}
          {exam.order_priority && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Độ ưu tiên</span>
              <span className="text-sm text-slate-900">
                {exam.order_priority === 'R' ? 'Routine' :
                  exam.order_priority === 'S' ? 'Stat (Cấp cứu)' :
                    exam.order_priority === 'A' ? 'ASAP' :
                      exam.order_priority === 'P' ? 'Preop' :
                        exam.order_priority === 'C' ? 'Callback' :
                          exam.order_priority}
              </span>
            </div>
          )}
          {exam.order_notes && (
            <div>
              <span className="block text-xs font-medium text-slate-600 mb-1">Ghi chú</span>
              <span className="text-sm text-slate-900 whitespace-pre-wrap">{exam.order_notes}</span>
            </div>
          )}
        </div>
      </div>

      {(exam.referring_physician_name || exam.requesting_physician_name) && (
        <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-200">
            <FiUserCheck className="text-primary text-lg" />
            <h2 className="text-sm font-semibold text-slate-900 m-0">Thông tin bác sĩ</h2>
          </div>
          <div className="space-y-3">
            {exam.referring_physician_name && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Bác sĩ chỉ định</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">
                  {exam.referring_physician_name}
                  {exam.referring_physician_id && (
                    <span className="text-slate-500"> ({exam.referring_physician_id})</span>
                  )}
                </span>
              </div>
            )}
            {exam.requesting_physician_name && (
              <div className="flex justify-between items-start">
                <span className="text-xs font-medium text-slate-600">Bác sĩ yêu cầu</span>
                <span className="text-sm text-slate-900 text-right flex-1 ml-4">
                  {exam.requesting_physician_name}
                  {exam.requesting_physician_id && (
                    <span className="text-slate-500"> ({exam.requesting_physician_id})</span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default ExamDetail;

